/**
 * Marketplace listing detail page (Kaggle-inspired).
 *
 * Layout:
 *
 *   [breadcrumbs]
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ HERO  big category-tinted glyph │ title / seller / cats │ subscribe panel
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ KPI STRIP   Records · Duration · First · Latest · Cadence · Subs
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ DATA OVER TIME  per-bin record-volume strip                 │
 *   │ RECENT VALUES   last 100 signals line chart                 │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ ABOUT (description)                                         │
 *   │ METADATA GRID  kind / frequency / interval / SLA / …        │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * The hero glyph + section accents are tinted with the listing's
 * top-level category color (see ``CategoryGlyphs.js``) so the page
 * inherits the brand of the data domain (Finance is teal, Crypto is
 * amber, etc.) without falling into the "every tile is a different
 * gradient" trap on the marketplace home.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import {
  Alert, Avatar, Box, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, Grid, Paper, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import StarIcon from '@mui/icons-material/Star';
import GroupsIcon from '@mui/icons-material/Groups';
import VerifiedIcon from '@mui/icons-material/Verified';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { DataSignalsIcon } from '../global/CustomIcons';
import LinkIcon from '@mui/icons-material/Link';
import GavelIcon from '@mui/icons-material/Gavel';
import CategoryIcon from '@mui/icons-material/Category';
import ScheduleIcon from '@mui/icons-material/Schedule';
import UpdateIcon from '@mui/icons-material/Update';
import TimerIcon from '@mui/icons-material/Timer';
import GroupIcon from '@mui/icons-material/Group';
import StorageIcon from '@mui/icons-material/Storage';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';
import {
  formatLargeNumber, formatNumber, secondsToHumanReadable,
} from '../../utils/utils';

import {
  fetchListing, fetchPreview, subscribe, clearMutations,
} from '../../redux/actions/marketplace/listings';
import { fetchRequest as fetchCategories } from '../../redux/actions/marketplace/categories';
import {
  fetchRequest as fetchRelatedBySellerRequest,
} from '../../redux/actions/marketplace/relatedBySeller';
import {
  fetchRequest as fetchRelatedByCategoryRequest,
  clearState as clearRelatedByCategoryState,
} from '../../redux/actions/marketplace/relatedByCategory';

import {
  KIND_LABELS, dailyFromMonthly, niceDate,
  buildCategoryIndex, categoryLabel,
  pricingModelOf, PRICING_MODEL_LABELS,
  updateFrequencyLabel, dataIntervalLabel, slaLabel,
  usdFromCredits, formatUSD, useUsdRate,
} from './marketplaceUtils';
import {
  glyphForCategory, accentForCategory,
} from './CategoryGlyphs';


// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const fmtTimestamp = (v) => {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short', day: '2-digit', year: 'numeric',
    });
  } catch (_e) { return null; }
};

// Always-relative helper for the "sub" line on date KPI tiles. We
// avoid niceDate here because it falls back to the absolute date for
// anything older than 30 days, which echoes the tile's value
// verbatim. Showing "5 years ago" vs the value's "Jan 15, 2020"
// gives the user two distinct facts at a glance.
const relativeFromNow = (v) => {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch (_e) { return null; }
};


// ---------------------------------------------------------------------------
// KpiTile — same visual language as SignalsKpiStrip, intentionally so
// that arriving from the Data Signals page feels consistent.
// ---------------------------------------------------------------------------

const KpiTile = ({ icon, value, label, sub, tone }) => {
  const theme = useTheme();
  const color = tone || theme.palette.primary.main;
  return (
    <Box
      sx={{
        position: 'relative',
        px: 1.5, py: 1.25,
        borderRadius: 1.75,
        background: `linear-gradient(135deg,
          ${alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.08)} 0%,
          ${alpha(color, theme.palette.mode === 'dark' ? 0.04 : 0.02)} 100%)`,
        border: `1px solid ${alpha(color, theme.palette.mode === 'dark' ? 0.28 : 0.18)}`,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${alpha(color, 0.6)}, transparent)`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.6}
             sx={{ color, mb: 0.25 }}>
        {icon}
        <Typography sx={{
          fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{
        fontSize: 17, fontWeight: 700, lineHeight: 1.15,
        color: theme.palette.text.primary,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
      }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{
          mt: 0.3, fontSize: 10, color: 'text.secondary',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// DataOverTimeStrip — record-count-per-bin volume bars across the
// whole series. Matches the DataDensityStrip visual on Data Signals.
// ---------------------------------------------------------------------------

const DataOverTimeStrip = ({ bins, accent }) => {
  const theme = useTheme();
  const baseColor = accent || theme.palette.primary.main;

  const data = useMemo(() => {
    if (!Array.isArray(bins) || bins.length === 0) return [];
    const counts = bins.map((b) =>
      typeof b?.count === 'number' ? b.count
        : typeof b?.frequency === 'number' ? b.frequency
        : 0,
    );
    const max = Math.max(1, ...counts);
    return bins.map((b, i) => ({
      h: counts[i] / max,
      count: counts[i],
      from: b?.from || null,
      to:   b?.to   || null,
    }));
  }, [bins]);

  if (data.length === 0) {
    return (
      <Paper variant="outlined" sx={{
        p: 4, textAlign: 'center', borderStyle: 'dashed',
        color: 'text.secondary',
      }}>
        <Typography variant="body2">
          No volume data available for this signal.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{
      height: 96,
      display: 'flex',
      alignItems: 'flex-end',
      gap: '3px',
    }}>
      {data.map((d, i) => (
        <Tooltip
          key={i}
          arrow placement="top"
          title={
            <Box sx={{ fontSize: 11 }}>
              {d.from && (
                <Box>
                  {new Date(d.from).toLocaleDateString(undefined, {
                    month: 'short', day: '2-digit', year: 'numeric',
                  })}
                  {d.to && d.to !== d.from && (
                    <>
                      {' → '}
                      {new Date(d.to).toLocaleDateString(undefined, {
                        month: 'short', day: '2-digit', year: 'numeric',
                      })}
                    </>
                  )}
                </Box>
              )}
              <Box sx={{ fontWeight: 700, mt: 0.25 }}>
                {formatNumber(d.count)} records
              </Box>
            </Box>
          }
        >
          <Box sx={{
            flex: 1, minWidth: 3,
            height: `${Math.max(6, d.h * 100)}%`,
            backgroundColor: alpha(baseColor, 0.4 + d.h * 0.5),
            borderRadius: '2px',
            cursor: 'default',
            transition: 'background-color 120ms ease',
            '&:hover': { backgroundColor: baseColor },
          }} />
        </Tooltip>
      ))}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// PreviewChart — last 100 signals, rendered as a line chart in the
// listing's category accent color.
// ---------------------------------------------------------------------------

const PreviewChart = ({ rows, accent }) => {
  const theme = useTheme();
  const data = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const points = rows
      .map((r) => {
        if (!r?.timestamp) return null;
        const d = new Date(r.timestamp);
        if (Number.isNaN(d.getTime())) return null;
        const v = Number(r?.value);
        if (!Number.isFinite(v)) return null;
        return { x: d, y: v };
      })
      .filter(Boolean)
      .sort((a, b) => a.x.getTime() - b.x.getTime());
    return points.length ? [{ id: 'preview', data: points }] : [];
  }, [rows]);

  if (data.length === 0) {
    return (
      <Paper variant="outlined" sx={{
        p: 4, textAlign: 'center', borderStyle: 'dashed',
        color: 'text.secondary',
      }}>
        <Typography variant="body2">No preview data available.</Typography>
      </Paper>
    );
  }
  return (
    <Box sx={{ height: 240 }}>
      <ResponsiveLine
        data={data}
        margin={{ top: 12, right: 24, bottom: 28, left: 48 }}
        xScale={{ type: 'time', precision: 'second' }}
        xFormat="time:%Y-%m-%d %H:%M"
        yScale={{ type: 'linear', stacked: false, min: 'auto', max: 'auto' }}
        curve="monotoneX"
        enablePoints={false}
        useMesh
        axisBottom={{
          tickSize: 0, tickPadding: 6, tickRotation: 0,
          format: (v) => {
            const d = v instanceof Date ? v : new Date(v);
            return Number.isNaN(d.getTime())
              ? ''
              : d.toLocaleDateString(undefined, {
                  month: 'short', day: '2-digit',
                });
          },
        }}
        axisLeft={{ tickSize: 0, tickPadding: 6 }}
        colors={[accent || theme.palette.primary.main]}
        gridYValues={4}
        animate={false}
        theme={{
          background: 'transparent',
          text: { fill: theme.palette.text.secondary, fontSize: 10 },
          grid: { line: { stroke: alpha(theme.palette.divider, 0.5),
                          strokeDasharray: '2 4' } },
          tooltip: {
            container: {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 6, fontSize: 11, padding: 6,
            },
          },
        }}
      />
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Metadata row helper
// ---------------------------------------------------------------------------

const MetaRow = ({ icon, label, value, accent }) => {
  const theme = useTheme();
  if (!value) return null;
  const tint = accent || theme.palette.primary.main;
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}
           sx={{ minWidth: 0 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.25,
        bgcolor: alpha(tint, 0.10),
        color: tint,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
          textTransform: 'uppercase', color: 'text.secondary',
          lineHeight: 1.2,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: 13, fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
};


// ---------------------------------------------------------------------------
// SectionTitle
// ---------------------------------------------------------------------------

const SectionTitle = ({ children, accent, hint }) => (
  <Stack direction="row" alignItems="baseline"
         sx={{ mb: 1.5, gap: 1.25 }}>
    <Box sx={{
      width: 3, height: 16, borderRadius: 1,
      backgroundColor: accent,
    }} />
    <Typography variant="subtitle1" fontWeight={700}>
      {children}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Stack>
);


// ---------------------------------------------------------------------------
// RelatedListingCard - compact tile used in the "Other signals by"
// rows on the listing detail page. One row scrolls horizontally on
// narrow widths so the section never wraps to a second line of cards.
// ---------------------------------------------------------------------------

const RelatedListingCard = ({ listing, onClick }) => {
  const theme = useTheme();
  const slug = (listing.categories || [])[0];
  const accent = accentForCategory(slug, theme.palette.primary.main);
  const Glyph  = glyphForCategory(slug);
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        minWidth: 220, maxWidth: 240, flexShrink: 0,
        cursor: 'pointer', borderRadius: 1.75,
        transition: 'border-color 120ms ease, transform 120ms ease',
        '&:hover': {
          borderColor: alpha(accent, 0.55),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center"
               sx={{ minWidth: 0, mb: 0.75 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1,
            bgcolor: alpha(accent, 0.1),
            color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Glyph size={16} />
          </Box>
          <Typography fontWeight={700} noWrap sx={{ fontSize: 13, flex: 1 }}>
            {listing.title || listing.signal_key}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center"
               sx={{ color: 'text.secondary' }}>
          <GroupIcon sx={{ fontSize: 12 }} />
          <Typography variant="caption" sx={{ fontSize: 11 }}>
            {(listing.subscribers_count || 0).toLocaleString()}
            {' subscriber'}{(listing.subscribers_count || 0) === 1 ? '' : 's'}
          </Typography>
        </Stack>
      </Box>
    </Card>
  );
};


// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const ListingDetailPage = ({
  fetchCategories, fetchListing, fetchPreview, subscribe, clearMutations,
  fetchRelatedBySeller, fetchRelatedByCategory, clearRelatedByCategory,
  categories, listingResp, listingLoading, listingError,
  previewResp, previewLoading,
  relatedBySellerItems, relatedByCategoryItems,
  mutations,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { listingId } = useParams();
  const usdRate = useUsdRate();

  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchListing(listingId);
    fetchPreview(listingId, 100);
  }, [fetchCategories, fetchListing, fetchPreview, listingId]);

  // ----- Related listings (same provider + same root category) ----------
  // We fan out two cheap /listings calls once the BE listing payload
  // lands, sorted by ``subscribers`` so the most-popular siblings
  // surface first. The current listing is filtered out client-side
  // so we don't have to invent a server-side "exclude" param.
  const sellerUid    = listingResp?.listing?.seller?.uid;
  const sellerScope  = listingResp?.listing?.seller?.scope;
  const rootCategory = (listingResp?.listing?.categories || [])[0];
  const rootSlug = rootCategory ? String(rootCategory).split('.')[0] : '';

  useEffect(() => {
    if (!sellerUid) return;
    fetchRelatedBySeller(sellerUid, sellerScope);
  }, [sellerUid, sellerScope, fetchRelatedBySeller]);

  useEffect(() => {
    if (!rootSlug) { clearRelatedByCategory(); return; }
    fetchRelatedByCategory(rootSlug);
  }, [rootSlug, fetchRelatedByCategory, clearRelatedByCategory]);

  const relatedBySeller = useMemo(
    () => (relatedBySellerItems || [])
      .filter((l) => l.listing_id !== listingId),
    [relatedBySellerItems, listingId],
  );
  const relatedByCategory = useMemo(
    () => (relatedByCategoryItems || [])
      .filter((l) => l.listing_id !== listingId)
      // Don't double-count entries the seller already owns -
      // they're shown in the "by provider" section above.
      .filter((l) => !sellerUid || l.seller?.uid !== sellerUid),
    [relatedByCategoryItems, listingId, sellerUid],
  );

  const listing = listingResp?.listing || null;
  const stats   = listingResp?.stats || null;
  const seller  = listing?.seller || {};
  const cats    = listing?.categories || [];
  // The BE tells us whether the calling workspace already has an
  // active subscription so we can swap the Subscribe CTA for a
  // disabled "Subscribed" state instead of showing a button that
  // 409s on click.
  const isSubscribed = !!listingResp?.subscription?.active;
  // ``would_be_free`` is set on the BE when a fresh subscribe would
  // short-circuit the charge (same owner / same org / org-scoped
  // listing). Used to swap the confirm dialog's pricing breakdown
  // for a "free for your team" message.
  const wouldBeFree = !!listingResp?.subscription?.would_be_free;
  const { bySlug } = useMemo(
    () => buildCategoryIndex(categories?.categories || []),
    [categories],
  );

  // Resolve the brand color + glyph from the listing's first
  // category. Listings carry their categories as dotted slugs
  // ("finance.equities") so accentForCategory walks up to the root.
  const primarySlug = cats[0] || null;
  const accent = accentForCategory(primarySlug, theme.palette.primary.main);
  const Glyph  = glyphForCategory(primarySlug);

  const monthly = listing?.price_monthly_credits || 0;
  const onetime = listing?.price_onetime_credits || 0;
  const daily = dailyFromMonthly(monthly);
  const pricingModel = listing ? pricingModelOf(listing) : 'free';

  // Derived stats - prefer the full-range binning result over the
  // cached listing fields when available (the cache can lag).
  const signalCount   = stats?.signal_count
                      ?? listing?.signal_count_cached ?? 0;
  const minTimestamp  = stats?.min_timestamp
                      ?? listing?.first_signal_ts_cached;
  const maxTimestamp  = stats?.max_timestamp
                      ?? listing?.latest_signal_ts_cached;
  const durationSec   = stats?.duration_seconds || 0;
  const lagSec        = stats?.lag_seconds || 0;
  const subscribers   = listing?.subscribers_count || 0;

  useEffect(() => {
    if (mutations.action === 'subscribe' && mutations.result && !mutations.isLoading) {
      setConfirmOpen(false);
      clearMutations();
      navigate('/signals');
    }
  }, [mutations, clearMutations, navigate]);

  if (listingLoading && !listing) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (listingError || !listing) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <PageBreadcrumbs items={[
          { text: 'Data Signals', link: '/signals', icon: <DataSignalsIcon /> },
          { text: 'Marketplace', link: '/marketplace' },
          { text: 'Not found' },
        ]} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {String(listingError || 'Listing not found')}
        </Alert>
      </Box>
    );
  }

  const sellerInitial = (seller.label || 'A').charAt(0).toUpperCase();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <PageBreadcrumbs items={[
        { text: 'Data Signals', link: '/signals', icon: <DataSignalsIcon /> },
        { text: 'Marketplace', link: '/marketplace' },
        { text: listing.title || listing.signal_key },
      ]} />

      {/* ──────────────────────────────────────────────────────────
          HERO
         ────────────────────────────────────────────────────────── */}
      <Card variant="outlined"
            sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}
                 alignItems={{ md: 'flex-start' }}>
            {/* Category glyph in the listing's brand color. */}
            {listing.icon_url ? (
              <Avatar src={listing.icon_url} variant="rounded"
                      sx={{ width: 80, height: 80, borderRadius: 2 }} />
            ) : (
              <Box sx={{
                width: 80, height: 80, borderRadius: 2,
                background: `linear-gradient(135deg,
                  ${alpha(accent, 0.16)} 0%,
                  ${alpha(accent, 0.04)} 100%)`,
                border: `1px solid ${alpha(accent, 0.25)}`,
                color: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Glyph size={42} />
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}
                     sx={{ mb: 0.5 }}>
                <Typography variant="h5" fontWeight={700}
                            sx={{ letterSpacing: -0.3 }}>
                  {listing.title || listing.signal_key}
                </Typography>
                <Chip
                  label={listing.listing_type === 'event' ? 'Event' : 'Signal'}
                  size="small"
                  color={listing.listing_type === 'event' ? 'error' : 'primary'}
                  sx={{ height: 22, fontWeight: 600, fontSize: 11 }}
                />
                {listing.featured && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 14 }} />}
                    label="Featured" size="small" color="warning"
                    sx={{ height: 22, fontWeight: 600, fontSize: 11 }}
                  />
                )}
                {/* Org-scope badge - signals to the buyer that this
                    is an internal-org listing (free for org members,
                    not visible publicly). */}
                {listing.visibility === 'org' && (
                  <Chip
                    icon={<GroupsIcon sx={{ fontSize: 14 }} />}
                    label="Organization" size="small"
                    sx={{
                      height: 22, fontWeight: 600, fontSize: 11,
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                      color: 'info.main',
                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                    }}
                  />
                )}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}
                     sx={{ mb: 1.25 }}>
                <Avatar sx={{
                  width: 22, height: 22, fontSize: 11,
                  bgcolor: alpha(accent, 0.12),
                  color: accent,
                }}>
                  {sellerInitial}
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  {seller.label || 'Anonymous'}
                </Typography>
                {seller.verified && (
                  <Tooltip title="Verified data provider">
                    <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                <Stack direction="row" alignItems="center" spacing={0.5}
                       sx={{ color: 'text.secondary' }}>
                  <GroupIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    {formatNumber(subscribers)} subscriber{subscribers === 1 ? '' : 's'}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap"
                     sx={{ rowGap: 0.5 }}>
                {/* Neutral outlined chips. The page already carries
                    the listing's category brand color via the top
                    accent strip, the icon glyph and the price panel -
                    repeating it on every chip read as too saturated. */}
                {cats.map((c) => (
                  <Chip
                    key={c}
                    label={categoryLabel(c, bySlug)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                ))}
                {(listing.tags || []).map((t) => (
                  <Chip key={t} label={`#${t}`} size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 22 }} />
                ))}
              </Stack>
            </Box>

            {/* Price + Subscribe panel. */}
            <Box sx={{
              minWidth: 240,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(accent, 0.04),
              border: `1px solid ${alpha(accent, 0.18)}`,
              alignSelf: { md: 'stretch' },
              display: 'flex', flexDirection: 'column',
            }}>
              <Typography variant="caption" color="text.secondary"
                          sx={{ fontWeight: 700, letterSpacing: 0.6,
                                textTransform: 'uppercase', fontSize: 10 }}>
                {PRICING_MODEL_LABELS[pricingModel]}
              </Typography>
              <Box sx={{ flex: 1, mt: 0.5 }}>
                {pricingModel === 'free' && (
                  <Typography sx={{
                    fontSize: 28, fontWeight: 700, color: 'success.main',
                    lineHeight: 1.1,
                  }}>
                    Free
                  </Typography>
                )}
                {onetime > 0 && (
                  <Box sx={{ mb: monthly > 0 ? 1 : 0 }}>
                    <Typography sx={{
                      fontSize: 26, fontWeight: 700,
                      color: accent, lineHeight: 1.1,
                    }}>
                      {formatUSD(usdFromCredits(onetime, usdRate))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      one-time
                    </Typography>
                  </Box>
                )}
                {monthly > 0 && (
                  <Box>
                    <Typography sx={{
                      fontSize: onetime > 0 ? 18 : 26,
                      fontWeight: 700,
                      color: onetime > 0 ? 'text.primary' : accent,
                      lineHeight: 1.1,
                    }}>
                      {formatUSD(usdFromCredits(monthly, usdRate))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ display: 'block' }}>
                      / month
                    </Typography>
                    <Typography variant="caption" color="text.disabled"
                                sx={{ display: 'block' }}>
                      ≈ {formatUSD(usdFromCredits(daily, usdRate))} / day
                    </Typography>
                  </Box>
                )}
              </Box>
              {isSubscribed ? (
                <CMButton
                  fullWidth disabled
                  sx={{ mt: 2 }}
                  startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                >
                  Subscribed
                </CMButton>
              ) : (
                <CMButton
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => setConfirmOpen(true)}
                >
                  Subscribe
                </CMButton>
              )}
              {isSubscribed && (
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 0.75,
                                  textAlign: 'center', fontSize: 11 }}>
                  Active in your current workspace — manage from{' '}
                  <Box
                    component="span"
                    onClick={() => navigate('/signals')}
                    sx={{ color: 'primary.main', cursor: 'pointer',
                          textDecoration: 'underline' }}
                  >
                    Data Signals
                  </Box>.
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ──────────────────────────────────────────────────────────
          KPI strip — Records / Duration / First / Latest / Cadence / Subs
         ────────────────────────────────────────────────────────── */}
      <Box sx={{
        mt: 2,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(6, 1fr)',
        },
        gap: 1.25,
      }}>
        <KpiTile
          icon={<StorageIcon sx={{ fontSize: 14 }} />}
          tone={accent}
          label="Records"
          value={formatLargeNumber(signalCount) || formatNumber(signalCount)}
          sub="across full series"
        />
        <KpiTile
          icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
          tone={theme.palette.info.main}
          label="Time Span"
          value={durationSec > 0 ? secondsToHumanReadable(durationSec) : '—'}
          sub={fmtTimestamp(minTimestamp)
            ? `from ${fmtTimestamp(minTimestamp)}`
            : null}
        />
        <KpiTile
          icon={<EventIcon sx={{ fontSize: 14 }} />}
          tone={theme.palette.success.main}
          label="First Signal"
          value={fmtTimestamp(minTimestamp) || '—'}
          sub={relativeFromNow(minTimestamp)}
        />
        <KpiTile
          icon={<EventAvailableIcon sx={{ fontSize: 14 }} />}
          tone={theme.palette.warning.main}
          label="Latest Signal"
          value={fmtTimestamp(maxTimestamp) || '—'}
          sub={relativeFromNow(maxTimestamp)}
        />
        <KpiTile
          icon={<SpeedIcon sx={{ fontSize: 14 }} />}
          tone={theme.palette.secondary?.main || '#A855F7'}
          label="Cadence"
          value={lagSec > 0 ? secondsToHumanReadable(Math.round(lagSec)) : '—'}
          sub={lagSec > 0 ? 'avg between signals' : null}
        />
        <KpiTile
          icon={<GroupIcon sx={{ fontSize: 14 }} />}
          tone={accent}
          label="Subscribers"
          value={formatNumber(subscribers)}
          sub="active right now"
        />
      </Box>

      {/* ──────────────────────────────────────────────────────────
          Data over time + Recent values
         ────────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
        <CardContent>
          <SectionTitle accent={accent}
                        hint={stats?.bins?.length
                          ? `${stats.bins.length} buckets across the full history`
                          : null}>
            Data over time
          </SectionTitle>
          <DataOverTimeStrip bins={stats?.bins || []} accent={accent} />
        </CardContent>
        <Divider />
        <CardContent>
          <SectionTitle accent={accent}
                        hint={previewResp?.count
                          ? `last ${previewResp.count} signals`
                          : null}>
            Recent values
          </SectionTitle>
          {previewLoading
            ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            : <PreviewChart rows={previewResp?.rows || []} accent={accent} />}
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}>
            Subscribed buyers see the full live stream inside their workspace.
          </Typography>
        </CardContent>
      </Card>

      {/* ──────────────────────────────────────────────────────────
          About + metadata grid
         ────────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
        <CardContent>
          {listing.description && (
            <>
              <SectionTitle accent={accent}>About this signal</SectionTitle>
              <Typography variant="body2"
                          sx={{ whiteSpace: 'pre-line', color: 'text.primary',
                                lineHeight: 1.6, mb: 2 }}>
                {listing.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          <SectionTitle accent={accent}>Signal metadata</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<UpdateIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Kind"
                       value={KIND_LABELS[listing.kind] || listing.kind} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Update frequency"
                       value={updateFrequencyLabel(listing.update_frequency) || '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<TimerIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Data interval"
                       value={dataIntervalLabel(listing.data_interval) || '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<GavelIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="SLA"
                       value={slaLabel(listing.sla) || '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<LinkIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Source"
                       value={listing.source_label || '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<DataObjectIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Signal key"
                       value={listing.signal_key} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Data type"
                       value={listing.data_type || '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetaRow icon={<EventAvailableIcon sx={{ fontSize: 16 }} />}
                       accent={accent}
                       label="Latest signal"
                       value={maxTimestamp
                         ? niceDate(maxTimestamp)
                         : '—'} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ──────────────────────────────────────────────────────────
          Other signals by the same provider
         ────────────────────────────────────────────────────────── */}
      {relatedBySeller.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <CardContent>
            <SectionTitle accent={accent}
                          hint={`${relatedBySeller.length} more from ${seller.label || 'this provider'}`}>
              More from {seller.label || 'this provider'}
            </SectionTitle>
            <Box sx={{
              display: 'flex', gap: 1.25,
              overflowX: 'auto', pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: 3,
              },
            }}>
              {relatedBySeller.slice(0, 12).map((l) => (
                <RelatedListingCard
                  key={l.listing_id}
                  listing={l}
                  onClick={() => navigate(`/marketplace/listings/${l.listing_id}`)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ──────────────────────────────────────────────────────────
          Related signals (same root category)
         ────────────────────────────────────────────────────────── */}
      {relatedByCategory.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <CardContent>
            <SectionTitle accent={accent}
                          hint={`Most subscribed in ${rootSlug || 'this category'}`}>
              Related signals
            </SectionTitle>
            <Box sx={{
              display: 'flex', gap: 1.25,
              overflowX: 'auto', pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: 3,
              },
            }}>
              {relatedByCategory.slice(0, 12).map((l) => (
                <RelatedListingCard
                  key={l.listing_id}
                  listing={l}
                  onClick={() => navigate(`/marketplace/listings/${l.listing_id}`)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ──────────────────────────────────────────────────────────
          Subscribe confirm dialog (unchanged behavior)
         ────────────────────────────────────────────────────────── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}
              maxWidth="sm" fullWidth>
        <DialogTitle>Subscribe to this signal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{listing.title || listing.signal_key}</strong> will be
            attached to your current workspace as a read-only signal.
          </DialogContentText>

          <Box sx={{
            mt: 2, p: 2, borderRadius: 1.5,
            bgcolor: alpha(accent, 0.04),
            border: `1px solid ${alpha(accent, 0.18)}`,
          }}>
            {wouldBeFree ? (
              // Same-owner / same-org / org-scoped listing - no money
              // moves regardless of the listing's nominal price.
              <Typography variant="body2" fontWeight={600}
                          sx={{ color: 'success.main' }}>
                Free for your team — no charge today, no recurring
                billing. {listing.visibility === 'org'
                  ? 'Org-scoped listings are always free for members.'
                  : 'You\'re subscribing to a signal owned by an account you have access to.'}
              </Typography>
            ) : (
              <>
                {onetime > 0 && (
                  <Stack direction="row" justifyContent="space-between"
                         alignItems="center" sx={{ mb: monthly > 0 ? 1 : 0 }}>
                    <Typography variant="body2">
                      <strong>One-time activation fee</strong>
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: 'block' }}>
                        Charged immediately. Non-refundable.
                      </Typography>
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      {formatUSD(usdFromCredits(onetime, usdRate))}
                    </Typography>
                  </Stack>
                )}
                {monthly > 0 && (
                  <Stack direction="row" justifyContent="space-between"
                         alignItems="center">
                    <Typography variant="body2">
                      <strong>Recurring</strong>
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ display: 'block' }}>
                        {formatUSD(usdFromCredits(daily, usdRate))} / day
                        {' '}(≈ {formatUSD(usdFromCredits(monthly, usdRate))} / month).
                        First daily charge runs in the next daily billing tick.
                      </Typography>
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      {formatUSD(usdFromCredits(daily, usdRate))} / day
                    </Typography>
                  </Stack>
                )}
                {pricingModel === 'free' && (
                  <Typography variant="body2" fontWeight={600}>
                    This signal is free to subscribe to.
                  </Typography>
                )}
              </>
            )}
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            You can cancel anytime.{' '}
            {(!wouldBeFree && monthly > 0)
              ? "If you cancel mid-day, today's daily charge is still applied in full and your access ends at the next daily billing tick."
              : 'Cancelling ends access at the next daily billing tick.'}
            {(!wouldBeFree && onetime > 0) && ' The upfront one-time fee is non-refundable.'}
          </Alert>
          {mutations.error && mutations.action === 'subscribe' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {String(mutations.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CMButtonOutlined onClick={() => setConfirmOpen(false)}>
            Cancel
          </CMButtonOutlined>
          <CMButton
            onClick={() => subscribe(listing.listing_id)}
            disabled={mutations.isLoading && mutations.action === 'subscribe'}
          >
            {mutations.isLoading && mutations.action === 'subscribe'
              ? 'Subscribing…'
              : 'Confirm subscribe'}
          </CMButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  categories:     state.marketplace_categories.response,
  listingResp:    state.marketplace_detail.response,
  listingLoading: state.marketplace_detail.isLoading,
  listingError:   state.marketplace_detail.error,
  previewResp:    state.marketplace_preview.response,
  previewLoading: state.marketplace_preview.isLoading,
  relatedBySellerItems:   state.marketplace_related_by_seller.response,
  relatedByCategoryItems: state.marketplace_related_by_category.response,
  mutations:      state.marketplace_mutations,
});

const mapDispatchToProps = (dispatch) => ({
  fetchCategories: () => dispatch(fetchCategories()),
  fetchListing:    (id) => dispatch(fetchListing(id)),
  fetchPreview:    (id, limit) => dispatch(fetchPreview(id, limit)),
  subscribe:       (id) => dispatch(subscribe(id)),
  clearMutations:  () => dispatch(clearMutations()),
  fetchRelatedBySeller: (sellerUid, sellerScope) =>
    dispatch(fetchRelatedBySellerRequest({ sellerUid, sellerScope })),
  fetchRelatedByCategory: (rootSlug) =>
    dispatch(fetchRelatedByCategoryRequest({ rootSlug })),
  clearRelatedByCategory: () => dispatch(clearRelatedByCategoryState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ListingDetailPage);
